import * as THREE from 'three';
import { gsap } from 'gsap';

export default class ScreenManager {
  constructor(scene) {
    this.scene = scene;
    this.screenGroup = new THREE.Group();
    this.scene.add(this.screenGroup);
    this.projectTextures = new Map();
    this.setupScreen();
    this.setupProjectTextures();
  }

  setupScreen() {
    // Create monitor frame
    const frameGeometry = new THREE.BoxGeometry(1.05, 0.65, 0.03);
    const frameMaterial = new THREE.MeshStandardMaterial({
      color: 0x222222,
      roughness: 0.3,
      metalness: 0.7
    });
    this.frame = new THREE.Mesh(frameGeometry, frameMaterial);
    this.frame.position.set(0, 1.3, -0.15);
    this.frame.rotation.set(0, Math.PI, 0);
    this.frame.castShadow = true;
    this.screenGroup.add(this.frame);

    // Create screen
    const screenGeometry = new THREE.PlaneGeometry(1, 0.6);
    
    // Load cursor texture
    const textureLoader = new THREE.TextureLoader();
    textureLoader.load('/cursor.png', (texture) => {
      const screenMaterial = new THREE.MeshBasicMaterial({ 
        color: new THREE.Color(0.2, 0.2, 0.2),
        transparent: false,
        map: texture,
        side: THREE.FrontSide
      });
      
      this.screen = new THREE.Mesh(screenGeometry, screenMaterial);
      this.screen.position.set(0, 1.3, -0.134);
      this.screen.rotation.set(0, 0, 0);
      this.screenGroup.add(this.screen);
    });
  }

  setupProjectTextures() {
    const projects = [
      { id: 'freehour', title: 'FreeHour', subtitle: 'Rebuilt + scaled a 30k DAU app' },
      { id: 'metaverse_architects', title: 'Metaverse Architects', subtitle: 'Co-founder: CTO/COO in practice' },
      { id: 'constitution_mt', title: 'Constitution.mt', subtitle: 'Interactive constitution + open analytics' },
      { id: 'kliem', title: 'KLIEM', subtitle: 'The Maltese Wordle (weekend build)' },
      { id: 'cayman_hurricane_watch', title: 'Cayman Hurricane Watch', subtitle: 'Storm tracking for Cayman society' },
      { id: 'careers_ky', title: 'Careers.ky', subtitle: 'Macro-to-micro view of the job market' },
      { id: 'legislation_ky', title: 'Legislation.ky', subtitle: 'Searchable Cayman Islands statute book' },
      { id: 'constitution_ky', title: 'Constitution.ky', subtitle: 'Interactive Cayman Islands constitution' },
      { id: 'cheap_ky', title: 'Cheap.ky', subtitle: 'Price transparency for the Cayman Islands' },
      { id: 'caybooks', title: 'CayBooks', subtitle: 'Bookkeeping for Cayman businesses' },
      { id: 'trade_quest', title: 'Trade Quest', subtitle: 'Performance-first social MMO (stealth)' }
    ];

    projects.forEach(({ id, title, subtitle }) => {
      // Generate a color based on the project name
      const hash = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      const hue = hash % 360;
      
      // Create a canvas for the project texture
      const canvas = document.createElement('canvas');
      canvas.width = 1024;
      canvas.height = 614;
      const ctx = canvas.getContext('2d');
      
      // Create a gradient background
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, `hsl(${hue}, 80%, 60%)`);
      gradient.addColorStop(1, `hsl(${(hue + 40) % 360}, 80%, 40%)`);
      
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Add a subtle pattern
      ctx.fillStyle = `hsla(${hue}, 70%, 65%, 0.1)`;
      for (let i = 0; i < 20; i++) {
        const x = Math.random() * canvas.width;
        const y = Math.random() * canvas.height;
        const size = 30 + Math.random() * 100;
        ctx.beginPath();
        ctx.arc(x, y, size, 0, Math.PI * 2);
        ctx.fill();
      }
      
      // Project title with enhanced styling
      ctx.shadowColor = 'rgba(0, 0, 0, 0.7)';
      ctx.shadowBlur = 10;
      ctx.shadowOffsetX = 5;
      ctx.shadowOffsetY = 5;
      
      // Project name in large text
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 72px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(title.toUpperCase(), canvas.width / 2, canvas.height / 2 - 40);
      
      ctx.font = 'normal 36px Arial';
      ctx.fillText(subtitle, canvas.width / 2, canvas.height / 2 + 40);
      
      // Create texture
      const texture = new THREE.CanvasTexture(canvas);
      texture.needsUpdate = true;
      this.projectTextures.set(id, texture);
    });
  }

  async showProjectOnScreen(project) {
    console.log('Displaying project:', project.title);

    try {
      let texture;
      if (this.projectTextures.has(project.id)) {
        console.log('Project texture found:', project.id);
        texture = this.projectTextures.get(project.id);
      } else {
        console.log('Project texture not found:', project.id);
        texture = await this.loadProjectTexture(project);
      }

      // Reset screen position and rotation
      this.screen.position.set(0, 1.3, -0.134);
      this.screen.rotation.set(0, 0, 0);

      // Apply texture with flash effect
      this.screen.material.color.set(0xffffff);
      this.screen.material.map = texture;
      this.screen.material.needsUpdate = true;

      // Flash effect
      gsap.fromTo(this.screen.material.color, 
        { r: 1.5, g: 1.5, b: 1.5 },
        { 
          r: 1, g: 1, b: 1,
          duration: 0.3,
          ease: "power2.out"
        }
      );

      // Update project card
      this.updateProjectCard(project);
    } catch (error) {
      console.error('Error showing project:', error);
      this.createFallbackTexture(project);
    }
  }

  updateProjectCard(project) {
    const cardTitle = document.querySelector('.card-title');
    const projectSummary = document.querySelector('.project-summary');
    const projectLinks = document.querySelector('.project-links');
    const techStack = document.querySelector('.tech-stack');
    const projectYear = document.querySelector('.project-year');

    if (cardTitle) cardTitle.textContent = project.title;

    // Project details mapping
    const projectDetails = {
      freehour: {
        summary: "Rebuilt the entire FreeHour mobile app in Flutter, replacing an expensive, slow, third‑party setup with a single high-performance codebase. Built analytics, helped shape the tech vision, and scaled team communication to make the org 'tech-ready'.",
        tech: ["Flutter", "Dart"],
        year: "2023–",
        links: [
          { label: "Website", href: "https://www.freehour.eu/" }
        ]
      },
      metaverse_architects: {
        summary: "Co‑founded Metaverse Architects during the metaverse boom. I was effectively CTO/COO: first engineer, built the team, shipped products end-to-end, and supported clients across delivery and leadership.",
        tech: ["Web", "3D", "Product delivery"],
        year: "2021–2023",
        links: [
          { label: "Press (TIME)", href: "https://time.com/6250249/chatgpt-metaverse/" }
        ]
      },
      constitution_mt: {
        summary: "Built Malta’s first interactive constitution to make rights and constitutional reform dialogue accessible. Open-sourced analytics so anyone can see what’s being searched and discussed.",
        tech: ["Web", "Open analytics"],
        year: "2024",
        links: [
          { label: "Visit", href: "https://www.constitution.mt/" }
        ]
      },
      kliem: {
        summary: "Built the Maltese Wordle as a fast, weekend ship: identify a gap, build quickly, and go to market.",
        tech: ["iOS", "Product sprint"],
        year: "2024",
        links: [
          { label: "App Store", href: "https://apps.apple.com/mt/app/kliem/id6749274930" }
        ]
      },
      cayman_hurricane_watch: {
        summary: "Built an app for Cayman society to track upcoming storms — shipped quickly with a practical, public-first mindset.",
        tech: ["iOS", "Product sprint"],
        year: "2024",
        links: [
          { label: "App Store", href: "https://apps.apple.com/gb/app/cayman-hurricane-watch/id6749749068" }
        ]
      },
      careers_ky: {
        summary: "Built a platform to give Caymanians a bird’s-eye view of the job market and let them zoom into roles with a clearer, more accessible ‘macro → micro’ exploration flow.",
        tech: ["Web platform"],
        year: "2024",
        links: [
          { label: "Visit", href: "https://careers.ky/" }
        ]
      },
      legislation_ky: {
        summary: "Free, open-source platform making 300+ Cayman Islands laws searchable and accessible. AI-powered semantic search, gazette tracking since 2004, hyperlinked cross-references, and amendment timelines across 8 practice areas.",
        tech: ["Web platform", "AI search", "Open source"],
        year: "2025–",
        links: [
          { label: "Visit", href: "https://legislation.ky/" }
        ]
      },
      constitution_ky: {
        summary: "Interactive digital platform for the Cayman Islands Constitution Order 2009. Navigate 9 parts and 125 sections with advanced search, legal cross-references, and visual data insights.",
        tech: ["Web platform", "Open source"],
        year: "2025–",
        links: [
          { label: "Visit", href: "https://constitution.ky/" }
        ]
      },
      cheap_ky: {
        summary: "Price comparison and shopping platform for the Cayman Islands. Compare prices across retailers, track grocery costs, and access user-generated price reports to shop smarter.",
        tech: ["Web platform"],
        year: "2025–",
        links: [
          { label: "Visit", href: "https://cheap.ky/" }
        ]
      },
      caybooks: {
        summary: "Bookkeeping and accounting platform built for Cayman Islands businesses. Simplifying financial management for the local market.",
        tech: ["Web platform"],
        year: "2025–",
        links: []
      },
      trade_quest: {
        summary: "A performance-first social MMO for trading. It’s not public yet — but the core thesis is serious practice, meaningful competition, and community at scale.",
        tech: ["Stealth"],
        year: "2025–",
        links: []
      }
    };

    const details = projectDetails[project.id];
    if (details) {
      if (projectSummary) projectSummary.textContent = details.summary;
      if (projectLinks) {
        const links = Array.isArray(details.links) ? details.links : [];
        projectLinks.innerHTML = links.length
          ? links.map(({ label, href }) => `<a href="${href}" target="_blank" rel="noopener noreferrer">${label}</a>`).join('')
          : '';
      }
      if (techStack) {
        techStack.innerHTML = details.tech.map(tech => 
          `<span>${tech}</span>`
        ).join('');
      }
      if (projectYear) projectYear.textContent = details.year;
    }
  }

  async loadProjectTexture(project) {
    try {
      const textureLoader = new THREE.TextureLoader();
      const texture = await new Promise((resolve, reject) => {
        textureLoader.load(
          `src/assets/projects/${project.id}.png`,
          resolve,
          undefined,
          reject
        );
      });
      this.projectTextures.set(project.id, texture);
      return texture;
    } catch (error) {
      console.error('Error loading project texture:', error);
      return this.createFallbackTexture(project);
    }
  }

  createFallbackTexture(project) {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');

    // Create gradient background
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, '#2a2a2a');
    gradient.addColorStop(1, '#1a1a1a');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Add project title
    ctx.font = 'bold 48px Arial';
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(project.title, canvas.width / 2, canvas.height / 2);

    const texture = new THREE.CanvasTexture(canvas);
    this.projectTextures.set(project.id, texture);
    return texture;
  }

  getScreen() {
    return this.screen;
  }

  getFrame() {
    return this.frame;
  }

  update() {
    // Add any screen-specific updates here
  }
} 